// result-verifier-contract-field-checks.ts
//
// Steps 6-6e of `verifyAttemptResult` — the result's contract fields checked
// against what the CONTROLLER expects (run_id, ticket_revision, input_hash,
// review_report, evidence-required, the ticket's acceptance rows). Split out of
// result-verifier.ts (plan 20260924-0205, 200-line budget). Same checks, same
// order; the first failing one wins, exactly as the inline chain did.
import type { AutopilotResult } from "./autopilot-result-schema.js";
import {
  checkEvidenceRequiredForAdapter,
  checkInputHashMatchesIfPresent,
  checkReviewReportDeclaredForReviewLocal,
  checkRunIdMatches,
  checkTicketRevisionMatches,
} from "./result-verifier-checks.js";
import type { VerifyAttemptResultInput } from "./result-verifier-types.js";
import { checkAcceptanceDoneHasEvidence } from "./ticket-acceptance-evidence-check.js";

/** First rejection reason among steps 6-6e, or `undefined` when all pass. */
export function firstContractFieldRejection(
  result: AutopilotResult,
  input: VerifyAttemptResultInput,
): string | undefined {
  const runIdReason = checkRunIdMatches(result.run_id, input.expectedRunId);
  if (runIdReason) return runIdReason;
  const revisionReason = checkTicketRevisionMatches(
    result.ticket_revision,
    input.expectedTicketRevision,
  );
  if (revisionReason) return revisionReason;
  // 6b. input_hash — controller-computed (D08); only rejects on an actual
  // MISMATCH, never on absence (see checkInputHashMatchesIfPresent).
  const inputHashReason = checkInputHashMatchesIfPresent(
    (result as { input_hash?: string }).input_hash,
    input.expectedInputHash,
  );
  if (inputHashReason) return inputHashReason;
  // 6c. review-local attempts must carry the report their own gate consumes.
  const reviewReportReason = checkReviewReportDeclaredForReviewLocal(
    (result as { review_report?: unknown }).review_report,
    input.isReviewLocalAttempt ?? false,
  );
  if (reviewReportReason) return reviewReportReason;

  // 6d (audit F2). BEFORE evaluateEvidence on purpose (result-verifier.ts calls
  // this whole function before it): that function iterates
  // `evidence[]`, so an EMPTY array walks straight past steps 7-9 producing no
  // reasons at all. The emptiness has to be caught by a check that does not
  // itself live inside the loop.
  const evidenceRequiredReason = checkEvidenceRequiredForAdapter(
    result.evidence ?? [],
    input.commandAdapter,
  );
  if (evidenceRequiredReason) return evidenceRequiredReason;

  // 6e (C1). The ticket's own acceptance rows: a condition ticked `done`
  // with nothing to point at. Reads the CONTROLLER's ticket, not the result
  // — see ticket-acceptance-evidence-check.ts for why that distinction is
  // the whole point. Skipped entirely for a ticket that declares none.
  return checkAcceptanceDoneHasEvidence(input.ticketAcceptance);
}
